import { useMemo, useState } from "react";
import { AppLayout } from "../../components/layout/Header";
import {
  ARTICLE_SECTIONS,
  BBS_CATEGORY_IDS,
  EVENT_KINDS,
  EVENT_SCOPES,
  HUB_ROLES,
  VENDOR_GROUPS,
  VISIBILITY,
  deleteArticle,
  deleteEvent,
  deletePost,
  deleteVendor,
  exportHubPack,
  getBlurbs,
  getSettings,
  getVisibility,
  listArticles,
  listEvents,
  listPosts,
  listVendors,
  saveArticle,
  saveBlurbs,
  saveEvent,
  savePost,
  saveSettings,
  saveVendor,
  saveVisibility,
} from "@hub";
import { toTitleCase } from "../../js/utils/titleCase";

const TABS = [
  { id: "site", label: "Site" },
  { id: "articles", label: "Support articles" },
  { id: "vendors", label: "Vendors" },
  { id: "board", label: "Board & events" },
  { id: "visibility", label: "Visibility" },
];

function downloadPack() {
  const pack = exportHubPack();
  const blob = new Blob([JSON.stringify(pack, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "dreamhome-hub-pack.json";
  link.click();
  URL.revokeObjectURL(url);
}

export default function AdminControls() {
  const [tab, setTab] = useState("site");
  const [pulse, setPulse] = useState(0);
  const [notice, setNotice] = useState("");
  const settings = useMemo(() => getSettings(), [pulse]);
  const blurbs = useMemo(() => getBlurbs(), [pulse]);
  const visibility = useMemo(() => getVisibility(), [pulse]);
  const articles = useMemo(() => listArticles(), [pulse]);
  const vendors = useMemo(() => listVendors(), [pulse]);
  const posts = useMemo(() => listPosts(), [pulse]);
  const events = useMemo(() => listEvents(), [pulse]);

  function refresh(message) {
    setPulse((n) => n + 1);
    if (message) setNotice(message);
  }

  return (
    <AppLayout variant="admin">
      <div className="page-header">
        <div>
          <h1>Controls</h1>
          <p className="page-lead">
            Content for DreamHomeJourney.online. Edits stay on this origin until a later import.
            No California DRE certification claims belong here.
          </p>
        </div>
      </div>
      {notice ? <p className="confirm-banner">{notice}</p> : null}

      <nav className="admin-tabs" aria-label="Control sections">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`messages-tab${tab === item.id ? " is-active" : ""}`}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {tab === "site" ? (
        <SitePanel
          settings={settings}
          blurbs={blurbs}
          onSaveSettings={(patch) => {
            saveSettings(patch);
            refresh("Site settings saved on this device.");
          }}
          onSaveBlurbs={(patch) => {
            saveBlurbs(patch);
            refresh("Marketing blurbs saved.");
          }}
        />
      ) : null}
      {tab === "articles" ? (
        <ArticlesPanel
          articles={articles}
          onSave={(row) => {
            saveArticle(row);
            refresh("Article saved.");
          }}
          onDelete={(id) => {
            deleteArticle(id);
            refresh("Article removed.");
          }}
        />
      ) : null}
      {tab === "vendors" ? (
        <VendorsPanel
          vendors={vendors}
          onSave={(row) => {
            saveVendor(row);
            refresh("Vendor saved.");
          }}
          onDelete={(id) => {
            deleteVendor(id);
            refresh("Vendor removed.");
          }}
        />
      ) : null}
      {tab === "board" ? (
        <BoardEventsPanel
          posts={posts}
          events={events}
          onSavePost={(row) => {
            savePost(row);
            refresh("Board post saved.");
          }}
          onDeletePost={(id) => {
            deletePost(id);
            refresh("Board post removed.");
          }}
          onSaveEvent={(row) => {
            saveEvent(row);
            refresh("Event saved.");
          }}
          onDeleteEvent={(id) => {
            deleteEvent(id);
            refresh("Event removed.");
          }}
        />
      ) : null}
      {tab === "visibility" ? (
        <VisibilityPanel
          visibility={visibility}
          onSave={(patch) => {
            saveVisibility(patch);
            refresh("Role-page visibility saved.");
          }}
        />
      ) : null}
    </AppLayout>
  );
}

function SitePanel({ settings, blurbs, onSaveSettings, onSaveBlurbs }) {
  const [site, setSite] = useState(settings);
  const [copy, setCopy] = useState(blurbs);

  return (
    <>
      <section className="desk-panel card">
        <h2>Site settings</h2>
        <label className="field">
          <span className="field-label">Site name</span>
          <input className="field-input" value={site.site_name || ""} onChange={(e) => setSite({ ...site, site_name: e.target.value })} />
        </label>
        <label className="field">
          <span className="field-label">Tagline</span>
          <input className="field-input" value={site.tagline || ""} onChange={(e) => setSite({ ...site, tagline: e.target.value })} />
        </label>
        <label className="field">
          <span className="field-label">Support intro</span>
          <textarea className="field-textarea" rows={3} value={site.support_intro || ""} onChange={(e) => setSite({ ...site, support_intro: e.target.value })} />
        </label>
        <button type="button" className="primary-btn" onClick={() => onSaveSettings(site)}>
          Save settings
        </button>
      </section>
      <section className="desk-panel card">
        <h2>Marketing blurbs</h2>
        {["pro", "clients", "board", "events"].map((key) => (
          <label key={key} className="field">
            <span className="field-label">{toTitleCase(key)}</span>
            <textarea className="field-textarea" rows={3} value={copy[key] || ""} onChange={(e) => setCopy({ ...copy, [key]: e.target.value })} />
          </label>
        ))}
        <div className="row" style={{ gap: "0.65rem" }}>
          <button type="button" className="primary-btn" onClick={() => onSaveBlurbs(copy)}>
            Save blurbs
          </button>
          <button type="button" className="ghost-btn" onClick={downloadPack}>
            Export content pack
          </button>
        </div>
      </section>
      <section className="desk-panel card">
        <h2>User Guide A–Z</h2>
        <p className="muted">
          The alphabetical directory is shared hub code. Journey renders it at /guide with role
          visibility. Support articles on this tab still feed role support pages. Do not fork
          messaging here.
        </p>
      </section>
    </>
  );
}

function ArticlesPanel({ articles, onSave, onDelete }) {
  const blank = { role: "buyer", section: "faq", title: "", body: "" };
  const [form, setForm] = useState(blank);

  return (
    <section className="desk-panel card">
      <h2>{form.id ? "Edit article" : "New article"}</h2>
      <div className="two-col">
        <label className="field">
          <span className="field-label">Role</span>
          <select className="field-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            {HUB_ROLES.map((role) => (
              <option key={role} value={role}>
                {toTitleCase(role)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field-label">Section</span>
          <select className="field-select" value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })}>
            {ARTICLE_SECTIONS.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="field">
        <span className="field-label">Title</span>
        <input className="field-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
      </label>
      <label className="field">
        <span className="field-label">Body</span>
        <textarea className="field-textarea" rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
      </label>
      <div className="row" style={{ gap: "0.65rem" }}>
        <button
          type="button"
          className="primary-btn"
          onClick={() => {
            if (!form.title.trim()) return;
            onSave(form);
            setForm(blank);
          }}
        >
          Save article
        </button>
        {form.id ? (
          <button type="button" className="ghost-btn" onClick={() => setForm(blank)}>
            Cancel edit
          </button>
        ) : null}
      </div>
      <ul className="admin-suggest-list">
        {articles.map((item) => (
          <li key={item.id} className="card admin-suggest-item">
            <p className="muted">
              {toTitleCase(item.role)} · {ARTICLE_SECTIONS.find((s) => s.id === item.section)?.label}
            </p>
            <p>{item.title}</p>
            <div className="row" style={{ gap: "0.5rem" }}>
              <button type="button" className="ghost-btn" onClick={() => setForm(item)}>
                Edit
              </button>
              <button type="button" className="ghost-btn" onClick={() => onDelete(item.id)}>
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function VendorsPanel({ vendors, onSave, onDelete }) {
  const blank = { audience: "client", group: "movers", name: "", summary: "", region: "San Diego County", url: "" };
  const [form, setForm] = useState(blank);
  const groups = VENDOR_GROUPS[form.audience] || [];

  return (
    <section className="desk-panel card">
      <h2>{form.id ? "Edit vendor" : "New vendor"}</h2>
      <div className="two-col">
        <label className="field">
          <span className="field-label">Audience</span>
          <select className="field-select" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value, group: VENDOR_GROUPS[e.target.value]?.[0]?.id || "" })}>
            <option value="pro">Pro</option>
            <option value="client">Client</option>
          </select>
        </label>
        <label className="field">
          <span className="field-label">Group</span>
          <select className="field-select" value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })}>
            {groups.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="field">
        <span className="field-label">Name</span>
        <input className="field-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </label>
      <label className="field">
        <span className="field-label">Summary</span>
        <textarea className="field-textarea" rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
      </label>
      <div className="two-col">
        <label className="field">
          <span className="field-label">Region</span>
          <input className="field-input" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} />
        </label>
        <label className="field">
          <span className="field-label">Link</span>
          <input className="field-input" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
        </label>
      </div>
      <button
        type="button"
        className="primary-btn"
        onClick={() => {
          if (!form.name.trim()) return;
          onSave(form);
          setForm(blank);
        }}
      >
        Save vendor
      </button>
      <ul className="admin-suggest-list">
        {vendors.map((item) => (
          <li key={item.id} className="card admin-suggest-item">
            <p className="muted">
              {item.audience === "pro" ? "Pro" : "Client"} · {item.group} · {item.region}
            </p>
            <p>{item.name}</p>
            <div className="row" style={{ gap: "0.5rem" }}>
              <button type="button" className="ghost-btn" onClick={() => setForm(item)}>
                Edit
              </button>
              <button type="button" className="ghost-btn" onClick={() => onDelete(item.id)}>
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function BoardEventsPanel({ posts, events, onSavePost, onDeletePost, onSaveEvent, onDeleteEvent }) {
  const blankPost = { category: "meetings", title: "", body: "", visibility: "public", author_name: "Site admin", author_role: "admin", flagged: false };
  const blankEvent = {
    title: "",
    kind: "workshop",
    scope: "local",
    starts_on: "",
    starts_at: "09:00",
    location: "",
    summary: "",
    visibility: "public",
    region: "San Diego County",
    url: "",
  };
  const [post, setPost] = useState(blankPost);
  const [event, setEvent] = useState(blankEvent);

  return (
    <>
      <section className="desk-panel card">
        <h2>{post.id ? "Edit board post" : "New board post"}</h2>
        <div className="two-col">
          <label className="field">
            <span className="field-label">Category</span>
            <select className="field-select" value={post.category} onChange={(e) => setPost({ ...post, category: e.target.value })}>
              {BBS_CATEGORY_IDS.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">Visibility</span>
            <select className="field-select" value={post.visibility} onChange={(e) => setPost({ ...post, visibility: e.target.value })}>
              {VISIBILITY.map((item) => (
                <option key={item} value={item}>
                  {toTitleCase(item)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="field">
          <span className="field-label">Title</span>
          <input className="field-input" value={post.title} onChange={(e) => setPost({ ...post, title: e.target.value })} />
        </label>
        <label className="field">
          <span className="field-label">Body</span>
          <textarea className="field-textarea" rows={4} value={post.body} onChange={(e) => setPost({ ...post, body: e.target.value })} />
        </label>
        <label className="field">
          <span className="field-label">
            <input type="checkbox" checked={Boolean(post.flagged)} onChange={(e) => setPost({ ...post, flagged: e.target.checked })} /> Flagged for moderation
          </span>
        </label>
        <button
          type="button"
          className="primary-btn"
          onClick={() => {
            if (!post.title.trim()) return;
            onSavePost(post);
            setPost(blankPost);
          }}
        >
          Save post
        </button>
        <ul className="admin-suggest-list">
          {posts.map((item) => (
            <li key={item.id} className="card admin-suggest-item">
              <p className="muted">
                {item.category}
                {item.flagged ? " · flagged" : ""} · {item.visibility}
              </p>
              <p>{item.title}</p>
              <div className="row" style={{ gap: "0.5rem" }}>
                <button type="button" className="ghost-btn" onClick={() => setPost(item)}>
                  Edit
                </button>
                <button type="button" className="ghost-btn" onClick={() => onSavePost({ ...item, flagged: !item.flagged })}>
                  {item.flagged ? "Clear flag" : "Flag"}
                </button>
                <button type="button" className="ghost-btn" onClick={() => onDeletePost(item.id)}>
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="desk-panel card">
        <h2>{event.id ? "Edit event" : "New event"}</h2>
        <label className="field">
          <span className="field-label">Title</span>
          <input className="field-input" value={event.title} onChange={(e) => setEvent({ ...event, title: e.target.value })} />
        </label>
        <div className="two-col">
          <label className="field">
            <span className="field-label">Kind</span>
            <select className="field-select" value={event.kind} onChange={(e) => setEvent({ ...event, kind: e.target.value })}>
              {EVENT_KINDS.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">Scope</span>
            <select className="field-select" value={event.scope} onChange={(e) => setEvent({ ...event, scope: e.target.value })}>
              {EVENT_SCOPES.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="two-col">
          <label className="field">
            <span className="field-label">Date</span>
            <input className="field-input" type="date" value={event.starts_on} onChange={(e) => setEvent({ ...event, starts_on: e.target.value })} />
          </label>
          <label className="field">
            <span className="field-label">Time</span>
            <input className="field-input" type="time" value={event.starts_at || "09:00"} onChange={(e) => setEvent({ ...event, starts_at: e.target.value })} />
          </label>
          <label className="field">
            <span className="field-label">Region</span>
            <input className="field-input" value={event.region || ""} onChange={(e) => setEvent({ ...event, region: e.target.value })} placeholder="San Diego County" />
          </label>
        </div>
        <label className="field">
          <span className="field-label">Visibility</span>
          <select className="field-select" value={event.visibility} onChange={(e) => setEvent({ ...event, visibility: e.target.value })}>
            {VISIBILITY.map((item) => (
              <option key={item} value={item}>
                {toTitleCase(item)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field-label">Location</span>
          <input className="field-input" value={event.location} onChange={(e) => setEvent({ ...event, location: e.target.value })} />
        </label>
        <label className="field">
          <span className="field-label">Summary</span>
          <textarea className="field-textarea" rows={3} value={event.summary} onChange={(e) => setEvent({ ...event, summary: e.target.value })} />
        </label>
        <button
          type="button"
          className="primary-btn"
          onClick={() => {
            if (!event.title.trim()) return;
            onSaveEvent(event);
            setEvent(blankEvent);
          }}
        >
          Save event
        </button>
        <ul className="admin-suggest-list">
          {events.map((item) => (
            <li key={item.id} className="card admin-suggest-item">
              <p className="muted">
                {item.starts_on} · {item.scope} · {item.kind}
              </p>
              <p>{item.title}</p>
              <div className="row" style={{ gap: "0.5rem" }}>
                <button type="button" className="ghost-btn" onClick={() => setEvent(item)}>
                  Edit
                </button>
                <button type="button" className="ghost-btn" onClick={() => onDeleteEvent(item.id)}>
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

function VisibilityPanel({ visibility, onSave }) {
  const [form, setForm] = useState(visibility);
  const keys = ["buyer", "seller", "agent", "broker", "admin", "vendors", "board", "events", "pro", "clients"];

  return (
    <section className="desk-panel card">
      <h2>Role-page visibility</h2>
      <p className="muted">Hide a Journey door without deleting its seed copy.</p>
      {keys.map((key) => (
        <label key={key} className="field">
          <span className="field-label">
            <input
              type="checkbox"
              checked={form[key] !== false}
              onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
            />{" "}
            {toTitleCase(key)}
          </span>
        </label>
      ))}
      <button type="button" className="primary-btn" onClick={() => onSave(form)}>
        Save visibility
      </button>
    </section>
  );
}
